import {FlusiaIcon} from './FlusiaIcon';
import {useFluPilot} from '../../lib/onboarding/consultant/useFluPilot';
import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMagnifyingGlass, faBars, faXmark, faSliders } from '@fortawesome/free-solid-svg-icons';
import { ArrowUp, ArrowDown, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useDynamicHeader } from '../../contexts/DynamicHeaderContext';
import type { HeaderFilter, HeaderFilterOption } from '../../contexts/DynamicHeaderContext';
import { MobileDrawer } from './MobileDrawer';
import { UserFilterDropdown } from '../layout/filters/UserFilterDropdown';
import { QuickCreateDrawer } from './QuickCreateDrawer';

interface MobileBottomMenuProps {
  onMenuClick?: () => void;
  isMenuOpen?: boolean;
  onSearchClick?: () => void; // Fallback per ricerca globale
}

export function MobileBottomMenu({
  onMenuClick,
  isMenuOpen = false,
  onSearchClick
}: MobileBottomMenuProps) {
  const fluPilot=useFluPilot();
  const { config } = useDynamicHeader();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  // Calcola se ci sono filtri/azioni contestuali
  const hasFilters = config?.filters && config.filters.length > 0;
  const hasSortOptions = config?.sortOptions && config.sortOptions.length > 0;
  const hasViewModes = config?.viewModes && config.viewModes.length > 0;
  const hasActions = config?.actions && config.actions.filter(a => !a.hidden).length > 0;
  const hasCustomRight = !!config?.customRight;
  const hasTabs = config?.tabs && config.tabs.length > 0;
  const hasPrimaryAction = config?.primaryAction && !config.primaryAction.hidden;

  // Conta filtri attivi
  const activeFiltersCount = config?.filters?.filter(f => {
    if (Array.isArray(f.value)) return f.value.length > 0;
    return f.value !== undefined && f.value !== null && f.value !== '';
  }).length || 0;

  const hasActiveFilters = activeFiltersCount > 0;

  // Trova il filtro di ricerca nel config (se esiste)
  const searchFilter = config?.filters?.find(f => f.type === 'search');

  // Gestisce il click sul tasto + contestuale
  const handleAddClick = () => {
    if (config?.primaryAction && !config.primaryAction.hidden) {
      // Azione contestuale dalla pagina
      config.primaryAction.onClick();
    } else if (hasActions || hasCustomRight) {
      // Apri drawer azioni per pagine dettaglio
      setIsActionsOpen(true);
    } else {
      // Apri drawer con opzioni di creazione rapida
      setIsQuickCreateOpen(true);
    }
  };

  // Gestisce la ricerca
  const handleSearchClick = () => {
    if (searchFilter) {
      setIsSearchExpanded(true);
      setLocalSearchQuery((searchFilter.value as string) || '');
    } else if (onSearchClick) {
      onSearchClick();
    }
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    if (searchFilter) {
      searchFilter.onChange('');
    }
    setLocalSearchQuery('');
  };

  const handleSearchChange = (value: string) => {
    setLocalSearchQuery(value);
    if (searchFilter) {
      searchFilter.onChange(value);
    }
  };

  return (
    <>
      {/* Mobile Tabs Strip - sopra il bottom menu */}
      {hasTabs && (
        <div
          className="sm:hidden fixed left-4 right-4 menu-mobile-dark rounded-t-2xl shadow-lg z-[9998] transition-all duration-300 ease-in-out"
          style={{ bottom: 'calc(max(1rem, env(safe-area-inset-bottom, 1rem)) + 56px)' }}
        >
          <div className="flex overflow-x-auto gap-1 px-2 py-1.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {config!.tabs!.map(tab => (
              <button
                key={tab.id}
                onClick={() => config!.onTabChange?.(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  config!.activeTab === tab.id
                    ? 'bg-black/10 dark:bg-white/20 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon && <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">{tab.icon}</span>}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="sm:hidden fixed left-4 right-4 menu-mobile-dark rounded-2xl shadow-lg z-[9999] transition-all duration-300 ease-in-out" style={{ bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
        <div className="flex items-center py-2 px-2">
          {isSearchExpanded ? (
            // Modalità ricerca espansa
            <>
              <div className="flex-1 mx-2 transition-all duration-300 ease-in-out">
                <input
                  type="text"
                  placeholder="Cerca..."
                  value={localSearchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm bg-transparent border-0 rounded-2xl focus:outline-none text-white placeholder-white/60"
                  autoFocus
                  style={{
                    background: 'transparent',
                    borderRadius: 'inherit'
                  }}
                />
              </div>
              <button
                onClick={handleSearchClose}
                aria-label={fluPilot?"Chiudi ricerca":undefined}
                className="flex items-center py-1.5 px-2 opacity-80 hover:opacity-100 hover:text-red-400 transition-colors duration-200"
              >
                {fluPilot?<FlusiaIcon name="close" size={20}/>:<FontAwesomeIcon icon={faXmark} className="h-5 w-5" />}
              </button>
            </>
          ) : (
            // Modalità normale: Menu | Filtri | Cerca | +
            <div className="flex items-center justify-evenly w-full">
              {/* Menu hamburger - sempre presente */}
              <button
                onClick={onMenuClick}
                aria-label={fluPilot?"Menu":undefined}
                className="flex items-center justify-center w-12 h-12 transition-colors duration-200"
              >
                {fluPilot?<FlusiaIcon name={isMenuOpen?"close":"menu"} size={20}/>:<FontAwesomeIcon icon={isMenuOpen ? faXmark : faBars} className="h-5 w-5 text-gray-800 dark:text-gray-200" />}
              </button>

              {/* Filtri contestuali - sempre presente ma disabilitato se non ci sono filtri */}
              <button
                onClick={() => (hasFilters || hasSortOptions) && setIsFiltersOpen(true)}
                aria-label={fluPilot?"Filtri e ordinamento":undefined}
                className={`flex items-center justify-center w-12 h-12 transition-colors duration-200 relative ${
                  !(hasFilters || hasSortOptions) ? 'opacity-30' : ''
                }`}
                disabled={!(hasFilters || hasSortOptions)}
              >
                {fluPilot?<FlusiaIcon name="settings" size={20} className={hasActiveFilters?'text-blue-500':''}/>:<FontAwesomeIcon
                  icon={faSliders}
                  className={`h-5 w-5 ${hasActiveFilters ? 'text-blue-500' : 'text-gray-800 dark:text-gray-200'}`}
                />}
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 flex items-center justify-center text-[10px] font-semibold bg-[#0f6cbd] text-white rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Ricerca - sempre presente */}
              <button
                onClick={handleSearchClick}
                aria-label={fluPilot?(searchFilter?"Cerca nella pagina":"Ricerca mobile"):undefined}
                className="flex items-center justify-center w-12 h-12 transition-colors duration-200"
              >
                {fluPilot?<FlusiaIcon name="search" size={20}/>:<FontAwesomeIcon icon={faMagnifyingGlass} className="h-5 w-5 text-gray-800 dark:text-gray-200" />}
              </button>

              {/* Tasto + - sempre presente */}
              <button
                onClick={handleAddClick}
                aria-label={fluPilot?"Crea":undefined}
                className="flex items-center justify-center w-12 h-12 transition-colors duration-200"
              >
                {fluPilot?<FlusiaIcon name="plus" size={20}/>:<FontAwesomeIcon icon={faPlus} className="h-5 w-5 text-gray-800 dark:text-gray-200" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Drawer filtri contestuali */}
      <MobileDrawer
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        title={hasSortOptions ? "Filtri e Ordinamento" : "Filtri"}
        height="auto"
        showHandle
        swipeToClose
      >
        <div className="space-y-4 pb-4">
          {/* View Modes */}
          {hasViewModes && config?.viewModes && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Vista</label>
              <div className="flex gap-2">
                {config.viewModes.map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => config.onViewModeChange?.(mode.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                      config.currentViewMode === mode.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'border-white/20 text-foreground'
                    }`}
                  >
                    <span className="[&>svg]:w-4 [&>svg]:h-4">{mode.icon}</span>
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sort Options */}
          {hasSortOptions && config?.sortOptions && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Ordina per</label>
              <div className="flex items-center gap-2">
                <select
                  value={config.currentSort?.field || config.sortOptions[0]?.field}
                  onChange={(e) => config.onSortChange?.({
                    field: e.target.value,
                    direction: config.currentSort?.direction || 'asc'
                  })}
                  className="flex-1 px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {config.sortOptions.map(opt => (
                    <option key={opt.field} value={opt.field}>{opt.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => config.onSortChange?.({
                    field: config.currentSort?.field || config.sortOptions![0]?.field,
                    direction: config.currentSort?.direction === 'asc' ? 'desc' : 'asc'
                  })}
                  className="flex items-center justify-center w-10 h-10 border border-white/20 rounded-lg text-foreground hover:bg-accent transition-colors"
                  title={config.currentSort?.direction === 'asc' ? 'Crescente' : 'Decrescente'}
                >
                  {config.currentSort?.direction === 'desc' ? (
                    <ArrowDown className="w-5 h-5" />
                  ) : (
                    <ArrowUp className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Filters - mostra label solo se ci sono anche sortOptions */}
          {hasFilters && config?.filters && (
            <div className="space-y-3">
              {hasSortOptions && (
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Filtri</label>
                  {hasActiveFilters && config.onClearFilters && (
                    <button
                      onClick={config.onClearFilters}
                      className="text-xs text-blue-600 dark:text-blue-400 font-medium"
                    >
                      Pulisci tutto
                    </button>
                  )}
                </div>
              )}
              {/* Pulsante pulisci quando non ci sono sortOptions */}
              {!hasSortOptions && hasActiveFilters && config.onClearFilters && (
                <div className="flex justify-end">
                  <button
                    onClick={config.onClearFilters}
                    className="text-xs text-blue-600 dark:text-blue-400 font-medium"
                  >
                    Pulisci tutto
                  </button>
                </div>
              )}
              <div className="space-y-3">
                {config.filters
                  .filter(f => f.type !== 'search') // Esclude il filtro search (gestito inline)
                  .map(filter => (
                    <MobileFilterRenderer key={filter.id} filter={filter} />
                  ))}
              </div>
            </div>
          )}

          {/* Custom Filters Row */}
          {config?.customFiltersRow && (
            <div className={hasSortOptions || (hasFilters && config?.filters?.some(f => f.type !== 'search')) ? "pt-2" : ""}>
              {config.customFiltersRow}
            </div>
          )}
        </div>
      </MobileDrawer>

      {/* Quick Create Drawer - per pagine senza primaryAction */}
      <QuickCreateDrawer
        isOpen={isQuickCreateOpen}
        onClose={() => setIsQuickCreateOpen(false)}
      />

      {/* Actions Drawer - per pagine dettaglio con azioni header */}
      <MobileDrawer
        isOpen={isActionsOpen}
        onClose={() => setIsActionsOpen(false)}
        title="Azioni"
        height="auto"
        showHandle
        swipeToClose
      >
        <div className="space-y-3 pb-4">
          {/* Custom Right (bottoni custom dell'header) */}
          {config?.customRight && (
            <div className="flex flex-wrap gap-2 pb-2 border-b border-white/10">
              {config.customRight}
            </div>
          )}

          {/* Actions strutturate */}
          {config?.actions?.filter(a => !a.hidden).map(action => (
            <button
              key={action.id}
              onClick={() => {
                action.onClick();
                setIsActionsOpen(false);
              }}
              className={`flex items-center gap-3 w-full px-4 py-3 text-sm rounded-lg border transition-colors ${
                action.variant === 'danger'
                  ? 'border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10'
                  : action.variant === 'primary'
                    ? 'border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10'
                    : 'border-white/20 text-foreground hover:bg-accent'
              }`}
            >
              {action.icon && <span className="[&>svg]:w-5 [&>svg]:h-5 flex-shrink-0">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      </MobileDrawer>
    </>
  );
}

// ============================================
// MOBILE FILTER RENDERER
// ============================================

function MobileFilterRenderer({ filter }: { filter: HeaderFilter }) {
  const hasValue = Array.isArray(filter.value) ? filter.value.length > 0 : (filter.value !== undefined && filter.value !== null && filter.value !== '');

  switch (filter.type) {
    case 'search':
      return (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
          <input
            type="text"
            value={filter.value || ''}
            onChange={(e) => filter.onChange(e.target.value)}
            placeholder={filter.placeholder || `Cerca ${filter.label.toLowerCase()}...`}
            className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );

    case 'select':
      return (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
          <select
            value={filter.value || ''}
            onChange={(e) => filter.onChange(e.target.value || undefined)}
            className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tutti</option>
            {filter.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      );

    case 'date-period': {
      const currentYear = new Date().getFullYear();
      const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
      const dateField = filter.dateField || 'created_at';
      const periodType = filter.periodType || 'custom';
      const selectedYear = filter.selectedYear || currentYear;
      const selectedQuarter = filter.selectedQuarter;
      const dateFrom = filter.dateFrom || '';
      const dateTo = filter.dateTo || '';

      const handlePeriodTypeChange = (type: 'year' | 'quarter' | 'custom') => {
        filter.onPeriodTypeChange?.(type);
        if (type !== 'custom') {
          filter.onDateFromChange?.('');
          filter.onDateToChange?.('');
        }
        if (type === 'year') {
          filter.onQuarterChange?.(null);
        }
      };

      const clearDateFilters = () => {
        filter.onPeriodTypeChange?.('custom');
        filter.onQuarterChange?.(null);
        filter.onDateFromChange?.('');
        filter.onDateToChange?.('');
      };

      const hasActiveDateFilter = periodType === 'year' || (periodType === 'quarter' && !!selectedQuarter) || (periodType === 'custom' && (!!dateFrom || !!dateTo));

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
            {hasActiveDateFilter && (
              <button onClick={clearDateFilters} className="text-xs text-blue-600 dark:text-blue-400">
                Cancella
              </button>
            )}
          </div>

          {/* Selettore campo */}
          <div className="space-y-1.5">
            <label className="text-[11px] text-muted-foreground">Campo</label>
            <div className="flex gap-2">
              {(['created_at', 'signed_at'] as const).map(field => (
                <button
                  key={field}
                  onClick={() => filter.onDateFieldChange?.(field)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    dateField === field
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'border-white/20 text-foreground active:bg-accent'
                  }`}
                >
                  {field === 'created_at' ? 'Creazione' : 'Firma'}
                </button>
              ))}
            </div>
          </div>

          {/* Selettore periodo */}
          <div className="space-y-1.5">
            <label className="text-[11px] text-muted-foreground">Periodo</label>
            <div className="flex gap-2">
              {(['year', 'quarter', 'custom'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => handlePeriodTypeChange(type)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    periodType === type
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'border-white/20 text-foreground active:bg-accent'
                  }`}
                >
                  {type === 'year' ? 'Anno' : type === 'quarter' ? 'Trimestre' : 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {/* Selettore anno */}
          {(periodType === 'year' || periodType === 'quarter') && (
            <div className="space-y-1.5">
              <label className="text-[11px] text-muted-foreground">Anno</label>
              <div className="flex gap-2">
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => filter.onYearChange?.(year)}
                    className={`flex-1 px-2 py-2 text-sm rounded-lg border transition-colors ${
                      selectedYear === year
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'border-white/20 text-foreground active:bg-accent'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selettore trimestre */}
          {periodType === 'quarter' && (
            <div className="space-y-1.5">
              <label className="text-[11px] text-muted-foreground">Trimestre</label>
              <div className="flex gap-2">
                {([1, 2, 3, 4] as const).map(q => (
                  <button
                    key={q}
                    onClick={() => filter.onQuarterChange?.(q)}
                    className={`flex-1 px-2 py-2 text-sm rounded-lg border transition-colors text-center ${
                      selectedQuarter === q
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'border-white/20 text-foreground active:bg-accent'
                    }`}
                  >
                    <div className="font-medium">Q{q}</div>
                    <div className="text-[10px] opacity-70">
                      {q === 1 && 'Gen-Mar'}
                      {q === 2 && 'Apr-Giu'}
                      {q === 3 && 'Lug-Set'}
                      {q === 4 && 'Ott-Dic'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date custom */}
          {periodType === 'custom' && (
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <label className="text-[11px] text-muted-foreground">Da</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => filter.onDateFromChange?.(e.target.value)}
                  className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1 space-y-1">
                <label className="text-[11px] text-muted-foreground">A</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => filter.onDateToChange?.(e.target.value)}
                  className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Scorciatoie */}
          <div className="space-y-1.5">
            <label className="text-[11px] text-muted-foreground">Scorciatoie</label>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Oggi', getRange: () => { const t = new Date().toISOString().split('T')[0]; return { from: t, to: t }; }},
                { label: '7 giorni', getRange: () => { const t = new Date(); const w = new Date(t); w.setDate(w.getDate() - 7); return { from: w.toISOString().split('T')[0], to: t.toISOString().split('T')[0] }; }},
                { label: '30 giorni', getRange: () => { const t = new Date(); const m = new Date(t); m.setDate(m.getDate() - 30); return { from: m.toISOString().split('T')[0], to: t.toISOString().split('T')[0] }; }},
                { label: 'Questo mese', getRange: () => { const t = new Date(); const s = new Date(t.getFullYear(), t.getMonth(), 1); return { from: s.toISOString().split('T')[0], to: t.toISOString().split('T')[0] }; }},
              ].map(shortcut => (
                <button
                  key={shortcut.label}
                  onClick={() => {
                    handlePeriodTypeChange('custom');
                    const range = shortcut.getRange();
                    filter.onDateFromChange?.(range.from);
                    filter.onDateToChange?.(range.to);
                  }}
                  className="px-3 py-1.5 text-xs rounded-lg border border-white/20 text-foreground active:bg-accent transition-colors"
                >
                  {shortcut.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    case 'date-range': {
      const dateFrom = filter.dateFrom || '';
      const dateTo = filter.dateTo || '';
      const hasDateValue = !!dateFrom || !!dateTo;

      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
            {hasDateValue && (
              <button
                onClick={() => { filter.onDateFromChange?.(''); filter.onDateToChange?.(''); }}
                className="text-xs text-blue-600 dark:text-blue-400"
              >
                Cancella
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1 space-y-1">
              <label className="text-[11px] text-muted-foreground">Da</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => filter.onDateFromChange?.(e.target.value)}
                className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-[11px] text-muted-foreground">A</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => filter.onDateToChange?.(e.target.value)}
                className="w-full px-3 h-10 text-sm border border-white/20 rounded-lg bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      );
    }

    case 'hierarchical-checkbox':
      return <MobileHierarchicalFilter filter={filter} />;

    case 'multi-checkbox':
    default:
      if (!filter.options || filter.options.length === 0) return null;

      const selectedValues = Array.isArray(filter.value) ? filter.value : [];

      const toggleOption = (optValue: string) => {
        if (selectedValues.includes(optValue)) {
          filter.onChange(selectedValues.filter(v => v !== optValue));
        } else {
          filter.onChange([...selectedValues, optValue]);
        }
      };

      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
            {hasValue && (
              <button
                onClick={() => filter.onChange([])}
                className="text-xs text-blue-600 dark:text-blue-400"
              >
                Cancella
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filter.options.map(opt => {
              const isSelected = selectedValues.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleOption(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'border-white/20 text-foreground active:bg-accent'
                  }`}
                >
                  {opt.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: opt.color }}
                    />
                  )}
                  {opt.icon && <span className="[&>svg]:w-4 [&>svg]:h-4">{opt.icon}</span>}
                  <span>{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        </div>
      );

    case 'user':
      return (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
          <UserFilterDropdown filter={filter} users={filter.users} />
        </div>
      );

    case 'custom':
      return filter.component ? (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
          {filter.component}
        </div>
      ) : null;
  }
}

// ============================================
// MOBILE HIERARCHICAL FILTER
// ============================================

function MobileHierarchicalFilter({ filter }: { filter: HeaderFilter }) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const options = filter.options || [];
  if (options.length === 0) return null;
  const selectedValues = Array.isArray(filter.value) ? filter.value : [];

  const { rootOptions, childrenMap } = useMemo(() => {
    const map = new Map<string | null, HeaderFilterOption[]>();
    options.forEach(opt => {
      const parentId = opt.parentId ?? null;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId)!.push(opt);
    });
    return { rootOptions: map.get(null) || [], childrenMap: map };
  }, [options]);

  const getDescendantIds = (parentId: string): string[] => {
    const children = childrenMap.get(parentId) || [];
    const ids: string[] = [];
    children.forEach(child => {
      ids.push(child.value);
      ids.push(...getDescendantIds(child.value));
    });
    return ids;
  };

  const toggleValue = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const descendantIds = getDescendantIds(value);
    if (isSelected) {
      const idsToRemove = new Set([value, ...descendantIds]);
      filter.onChange(selectedValues.filter((v: string) => !idsToRemove.has(v)));
    } else {
      filter.onChange([...new Set([...selectedValues, value, ...descendantIds])]);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasValue = selectedValues.length > 0;

  const renderOption = (option: HeaderFilterOption, level: number = 0) => {
    const children = childrenMap.get(option.value) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(option.value);
    const isSelected = selectedValues.includes(option.value);

    return (
      <div key={option.value}>
        <div
          className="flex items-center gap-2 py-1"
          style={{ paddingLeft: `${level * 20}px` }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(option.value)}
              className="w-5 h-5 flex items-center justify-center flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="w-5 flex-shrink-0" />
          )}
          <button
            onClick={() => toggleValue(option.value)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors flex-1 text-left ${
              isSelected
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'border-white/20 text-foreground active:bg-accent'
            }`}
          >
            {option.color && (
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.color }} />
            )}
            <span className="flex-1">{option.label}</span>
            {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
          </button>
        </div>
        {hasChildren && isExpanded && children.map(child => renderOption(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">{filter.label}</label>
        {hasValue && (
          <button onClick={() => filter.onChange([])} className="text-xs text-blue-600 dark:text-blue-400">
            Cancella
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {rootOptions.map(option => renderOption(option, 0))}
      </div>
    </div>
  );
}
